"use client"

import { useRef, useState } from "react"
import NextImage from "next/image"
import { motion, useInView } from "framer-motion"
import { Award, Users, TrendingUp, BarChart2 } from "lucide-react"
import Link from "next/link"

// import awardWinDesign from "@/public/whyChooseUs/awardWinning.png";
import awardWinDesign from "@/public/HeroReportsThumbnail/pdfSqIntegratedThumb.png";
// import awardWinDesign from "@/public/whyChooseUs/awardWinDesign.jpg";
import dataVisuals from "@/public/whyChooseUs/dataVisuals.jpg";
import resultDriven from "@/public/whyChooseUs/resultDriven1.jpg";

const reasons = [
  {
    icon: <Award className="w-6 h-6" />,
    title: "Award-Winning Design",
    description: "Our reports have received recognition for their exceptional design and visual storytelling.",
  },
  {
    icon: <Users className="w-6 h-6" />,
    title: "Expert Team",
    description: "Our team combines design expertise with subject matter experts to design impactful reports.",
  },
  {
    icon: <TrendingUp className="w-6 h-6" />,
    title: "Results-Driven Approach",
    description: "We focus on designing reports that drive stakeholder engagement and connect consumers.",
  },
  {
    icon: <BarChart2 className="w-6 h-6" />,
    title: "Data Visualization",
    description: "We transform complex data into clear, compelling visual narratives that tell your story.",
  },
]

export default function WhyChooseUs() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(sectionRef, { once: false, amount: 0.2, margin: "-100px" })
  const [activeTab, setActiveTab] = useState(0)

  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 1, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  }

  const imageVariants = {
    hidden: { opacity: 1, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  }

  const tabImages = [
    awardWinDesign,
    "https://www.bunnx.com/_next/static/media/ofc.568836d2.JPG",
    resultDriven,
    dataVisuals,

  ]

  return (
    <section
      ref={sectionRef}
      className="py-16 md:py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden"
      id="why-choose-us"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-dots-pattern opacity-5"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
          className="text-center mb-16"
        >
          {/* <motion.h2
            variants={itemVariants}
            className="inline-block mb-4 px-4 py-2 rounded-full bg-gray-200 text-gray-800 font-medium text-sm"
          >
            Why Business Choose Us
          </motion.h2> */}

          <motion.h2 variants={itemVariants} className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {/* Combine Design Excellence with Strategic Communication */}
            Why Leading Companies Trust Annual ESG

          </motion.h2>

          <motion.p variants={itemVariants} className="text-lg text-gray-600 max-w-4xl mx-auto">
            At Annual ESG, we don’t just design reports. We craft brandaligned sustainability stories that turn compliance into
            influence. By merging world-class visual design with
            strategic content, we help you communicate your impact
            clearly, confidently, and beautifully.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
        >
          {/* Left Column - Interactive Image */}
          <motion.div variants={imageVariants} className="relative order-2 lg:order-1">
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0"
              >
                <NextImage src={tabImages[activeTab]} alt={reasons[activeTab].title} fill className="object-cover" />
                <div className="absolute inset-0 bg-gradientto-t from-gray-900/30 to-transparent"></div>
              </motion.div>
            </div>

            {/* Floating stats card */}
            <motion.div
              className="absolute -bottom-10 -right-10 bg-white rounded-2xl p-6 shadow-xl border border-gray-200 w-64"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-500">Client Satisfaction</span>
                <span className="text-gray-900 font-bold">98%</span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gray-900 rounded-full" style={{ width: "96%" }}></div>
              </div>
              <div className="mt-4">
                <p className="text-gray-900 font-medium">50+ Reports Designed</p>
                <p className="text-gray-500 text-sm">For global clients</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - Interactive Tabs */}
          <motion.div variants={containerVariants} className="space-y-8 order-1 lg:order-2">

            <div className="space-y-4">
              {reasons.map((reason, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className={`p-6 rounded-xl cursor-pointer transition-all duration-300 ${activeTab === index
                    ? "bg-gray-900 text-white shadow-lg"
                    : "bg-white hover:bg-gray-50 border border-gray-100"
                    }`}
                  onClick={() => setActiveTab(index)}
                  whileHover={{ y: -5 }}
                >
                  <div className="flex items-start">
                    <div className={`mr-4 p-3 rounded-full ${activeTab === index ? "bg-white/20" : "bg-gray-100"}`}>
                      <div className={activeTab === index ? "text-white" : "text-gray-900"}>{reason.icon}</div>
                    </div>
                    <div>
                      <h4 className={`text-xl font-bold mb-2 ${activeTab === index ? "text-white" : "text-gray-900"}`}>
                        {reason.title}
                      </h4>
                      <p className={`${activeTab === index ? "text-white/90" : "text-gray-600"}`}>
                        {reason.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div variants={itemVariants} className="pt-4">
              <Link href="#contact" className="bg-gray-800 hover:bg-gray-900 text-white px-6 py-3 md:px-8 md:py-4 text-base md:text-lg rounded-full shadow-lg transition-all duration-300 hover:shadow-xl">Get in Touch</Link>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
